import { useState, useEffect } from 'react';
import { X, Upload, Wand2, Trash2, Loader2, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductModal = ({
  isOpen,
  onClose,
  product,
  categories,
  isArabic,
  onSave,
  saving
}) => {
  const [formData, setFormData] = useState({
    nameAr: '',
    nameEn: '',
    descAr: '',
    descEn: '',
    category: categories[0]?.id || '',
    images: [],
    isVisible: true
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showAiHelper, setShowAiHelper] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [generateImage, setGenerateImage] = useState(true);

  // Reset form when product changes
  useEffect(() => {
    if (product) {
      setFormData({
        nameAr: product.nameAr || '',
        nameEn: product.nameEn || '',
        descAr: product.descAr || '',
        descEn: product.descEn || '',
        category: product.category || categories[0]?.id || '',
        images: product.images || [],
        isVisible: product.isVisible !== undefined ? product.isVisible : true
      });
    } else {
      setFormData({
        nameAr: '',
        nameEn: '',
        descAr: '',
        descEn: '',
        category: categories[0]?.id || '',
        images: [],
        isVisible: true
      });
    }
  }, [product, categories]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);

      const token = localStorage.getItem('admin_token');
      
      const res = await fetch(`${API}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataObj
      });

      if (res.ok) {
        const data = await res.json();
        const imageUrl = data.url.startsWith('http') ? data.url : `${BACKEND_URL}${data.url}`;
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, imageUrl]
        }));
      }
    } catch (err) {
      console.error('Upload failed:', err);
    }
    setUploadingImage(false);
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleAiGenerate = async () => {
    if (!aiDescription.trim()) return;
    
    setAiGenerating(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      const res = await fetch(`${API}/ai/generate-product`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          description: aiDescription,
          category: formData.category,
          generateImage: generateImage
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setFormData(prev => ({
            ...prev,
            nameAr: data.nameAr || prev.nameAr,
            nameEn: data.nameEn || prev.nameEn,
            descAr: data.descAr || prev.descAr,
            descEn: data.descEn || prev.descEn,
            images: data.image ? [...prev.images, `${BACKEND_URL}${data.image}`] : prev.images
          }));
          setShowAiHelper(false);
          setAiDescription('');
        } else {
          alert(data.error || 'AI generation failed');
        }
      }
    } catch (err) {
      console.error('AI generation failed:', err);
      alert(isArabic ? 'فشل في توليد المنتج' : 'AI generation failed');
    }
    setAiGenerating(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
            <h3 className="text-xl font-bold text-slate-900">
              {product ? (isArabic ? 'تعديل المنتج' : 'Edit Product') : (isArabic ? 'إضافة منتج جديد' : 'Add New Product')}
            </h3>
            <div className="flex items-center gap-2">
              {!product && (
                <button
                  type="button"
                  onClick={() => setShowAiHelper(!showAiHelper)}
                  className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium hover:from-purple-600 hover:to-pink-600 transition-all"
                  data-testid="ai-helper-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  {isArabic ? 'مساعدة AI' : 'AI Help'}
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* AI Helper Panel */}
          <AnimatePresence>
            {showAiHelper && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-200">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Wand2 className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">
                        {isArabic ? 'مساعد المنتجات الذكي' : 'AI Product Assistant'}
                      </h4>
                      <p className="text-sm text-slate-500">
                        {isArabic ? 'أدخل وصفاً بسيطاً وسيقوم الذكاء الاصطناعي بتوليد التفاصيل' : 'Enter a simple description and AI will generate the details'}
                      </p>
                    </div>
                  </div>
                  
                  <textarea
                    value={aiDescription}
                    onChange={(e) => setAiDescription(e.target.value)}
                    placeholder={isArabic ? 'مثال: جهاز قياس ضغط الدم الرقمي مع شاشة كبيرة...' : 'Example: Digital blood pressure monitor with large display...'}
                    className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    rows={3}
                    data-testid="ai-description-input"
                  />
                  
                  <div className="flex items-center gap-4 mt-4">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={generateImage}
                        onChange={(e) => setGenerateImage(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      {isArabic ? 'توليد صورة للمنتج' : 'Generate product image'}
                    </label>
                    {generateImage && (
                      <span className="text-xs text-purple-600">
                        {isArabic ? '(قد يستغرق 30-60 ثانية)' : '(May take 30-60 seconds)'}
                      </span>
                    )}
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleAiGenerate}
                    disabled={aiGenerating || !aiDescription.trim()}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50"
                    data-testid="ai-generate-btn"
                  >
                    {aiGenerating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {isArabic ? 'جاري التوليد...' : 'Generating...'}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        {isArabic ? 'توليد باستخدام AI' : 'Generate with AI'}
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Names */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {isArabic ? 'الاسم بالعربية' : 'Arabic Name'} *
                </label>
                <input
                  type="text"
                  value={formData.nameAr}
                  onChange={(e) => handleChange('nameAr', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent"
                  required
                  dir="rtl"
                  data-testid="product-name-ar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {isArabic ? 'الاسم بالإنجليزية' : 'English Name'} *
                </label>
                <input
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) => handleChange('nameEn', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent"
                  required
                  dir="ltr"
                  data-testid="product-name-en"
                />
              </div>
            </div>

            {/* Descriptions */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {isArabic ? 'الوصف بالعربية' : 'Arabic Description'}
                </label>
                <textarea
                  value={formData.descAr}
                  onChange={(e) => handleChange('descAr', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent"
                  dir="rtl"
                  data-testid="product-desc-ar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {isArabic ? 'الوصف بالإنجليزية' : 'English Description'}
                </label>
                <textarea
                  value={formData.descEn}
                  onChange={(e) => handleChange('descEn', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent"
                  dir="ltr"
                  data-testid="product-desc-en"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {isArabic ? 'الفئة' : 'Category'} *
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent"
                required
                data-testid="product-category"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {isArabic ? cat.nameAr : cat.nameEn}
                  </option>
                ))}
              </select>
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {isArabic ? 'الصور' : 'Images'}
              </label>
              
              <div className="grid grid-cols-4 gap-4 mb-4">
                {formData.images.map((img, idx) => (
                  <div key={idx} className="relative aspect-square bg-slate-100 rounded-xl overflow-hidden">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                {/* Upload Button */}
                <label className="aspect-square border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-slate-400 transition-colors">
                  {uploadingImage ? (
                    <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-slate-400 mb-2" />
                      <span className="text-xs text-slate-400">
                        {isArabic ? 'رفع صورة' : 'Upload'}
                      </span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                </label>
              </div>
            </div>

            {/* Visibility */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isVisible"
                checked={formData.isVisible}
                onChange={(e) => handleChange('isVisible', e.target.checked)}
                className="w-5 h-5 rounded border-slate-300"
              />
              <label htmlFor="isVisible" className="text-sm text-slate-700">
                {isArabic ? 'المنتج مرئي للعملاء' : 'Product visible to customers'}
              </label>
            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 transition-colors"
              >
                {isArabic ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-3 bg-black text-white rounded-xl hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-product-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isArabic ? 'جاري الحفظ...' : 'Saving...'}
                  </>
                ) : (
                  isArabic ? 'حفظ المنتج' : 'Save Product'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProductModal;
